import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerDetails } from '@/types/partners';


export function usePartnerDetails() {
  const { user, profile } = useAuth();

  // For partner_client users, use their partner_id; otherwise use their own user.id
  const partnerUserId = profile?.user_type === 'partner_client' && profile.partner_id
    ? profile.partner_id
    : user?.id;

  // Only enable query if user might be a partner or partner client
  const shouldFetch = !!user && !!partnerUserId && (
    profile?.user_type === 'partner_client' || 
    profile?.partner_id !== null
  );

  return useQuery({
    queryKey: ['partner-details', partnerUserId],
    queryFn: async (): Promise<PartnerDetails | null> => {
      if (!user) throw new Error('User not authenticated');
      if (!partnerUserId) return null;

      const { data, error } = await supabase
        .rpc('get_partner_details', { p_user_id: partnerUserId });

      if (error) {
        // If no partner found, return null (not an error)
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return data?.[0] || null;
    },
    enabled: shouldFetch,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
}
